import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import api, { inr } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Check, CreditCard, Truck } from "lucide-react";

export default function CheckoutPage() {
  const { cart, sessionId, clearCart } = useCart();
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(null);

  // Gate: require sign-in before checkout
  useEffect(() => {
    if (!checking && !user) {
      navigate("/login", { state: { from: "/checkout" }, replace: true });
    }
  }, [checking, user, navigate]);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    payment_method: "razorpay",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
      }));
    }
  }, [user]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) return toast.error("Your cart is empty");
    setLoading(true);
    try {
      const items = cart.items.map(({ product, quantity }) => ({ product_id: product.id, quantity }));
      const { data: order } = await api.post(`/checkout/create-order`, {
        session_id: sessionId,
        items,
        ...form,
      });

      // Mock payment delay
      await new Promise((r) => setTimeout(r, 1200));

      const { data: verified } = await api.post(`/checkout/verify`, {
        order_id: order.order_id,
        razorpay_payment_id: `pay_mock_${Math.random().toString(36).slice(2,12)}`,
        razorpay_signature: `sig_mock_${Math.random().toString(36).slice(2,12)}`,
      });
      toast.success("Payment successful (mock mode)");
      setPaid(verified.order);
      await clearCart();
    } catch (err) {
      console.error(err);
      toast.error("Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (paid) {
    return (
      <div data-testid="checkout-success" className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-welljio-soft text-welljio-green mx-auto flex items-center justify-center mb-6">
          <Check className="w-7 h-7" strokeWidth={2} />
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-4">Order confirmed.</h1>
        <p className="text-welljio-muted mb-8">Order ID: <span className="text-welljio-ink font-medium">{paid.id}</span></p>
        <div className="bg-white border border-slate-100 rounded-3xl p-8 text-left max-w-xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3">Summary</p>
          {paid.items.map((it) => (
            <div key={it.product_id} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="text-sm">{it.name} × {it.quantity}</span>
              <span className="text-sm">{inr(it.line_total)}</span>
            </div>
          ))}
          <div className="flex justify-between mt-4 font-heading font-semibold">
            <span>Total paid</span><span>{inr(paid.total)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          <Link to="/shop" className="inline-block bg-welljio-ink text-white rounded-full px-7 py-4 font-medium hover:bg-slate-800">Continue shopping</Link>
          {user && (
            <Link to={`/account/orders/${paid.id}`} data-testid="success-view-order-link" className="inline-block bg-welljio-highlight text-welljio-accent rounded-full px-7 py-4 font-medium hover:bg-sky-100">Track this order</Link>
          )}
        </div>
        {!user && (
          <p className="text-sm text-welljio-muted mt-6">
            <Link to="/register" className="text-welljio-accent hover:underline">Create an account</Link> to track future orders.
          </p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-heading text-5xl font-bold tracking-tight mb-6">Checkout</h1>
      {!user && (
        <div className="mb-8 bg-welljio-highlight border border-sky-100 rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-welljio-ink">Have an account? <Link to="/login" state={{ from: "/checkout" }} className="text-welljio-accent font-medium hover:underline">Sign in</Link> to track this order.</p>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 space-y-8" data-testid="checkout-form">
          <section className="bg-white border border-slate-100 rounded-3xl p-7 space-y-4">
            <h2 className="font-heading text-2xl font-semibold mb-3">Contact</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required value={form.customer_name} onChange={change("customer_name")} placeholder="Full name" className="border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-name" />
              <input required type="email" value={form.customer_email} onChange={change("customer_email")} placeholder="Email" className="border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-email" />
            </div>
            <input required value={form.customer_phone} onChange={change("customer_phone")} placeholder="Phone (+91)" className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-phone" />
          </section>

          <section className="bg-white border border-slate-100 rounded-3xl p-7 space-y-4">
            <h2 className="font-heading text-2xl font-semibold mb-3">Shipping</h2>
            <input required value={form.address_line1} onChange={change("address_line1")} placeholder="Address line 1" className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-address1" />
            <input value={form.address_line2} onChange={change("address_line2")} placeholder="Address line 2 (optional)" className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-address2" />
            <div className="grid sm:grid-cols-3 gap-3">
              <input required value={form.city} onChange={change("city")} placeholder="City" className="border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-city" />
              <input required value={form.state} onChange={change("state")} placeholder="State" className="border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-state" />
              <input required value={form.pincode} onChange={change("pincode")} placeholder="PIN code" className="border border-slate-200 rounded-2xl px-5 py-3.5 text-sm outline-none focus:border-welljio-accent" data-testid="checkout-pincode" />
            </div>
          </section>

          <section className="bg-white border border-slate-100 rounded-3xl p-7 space-y-4">
            <h2 className="font-heading text-2xl font-semibold mb-3">Payment</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <label data-testid="payment-razorpay" className={`cursor-pointer border rounded-2xl p-5 flex items-start gap-3 ${form.payment_method === "razorpay" ? "border-welljio-ink bg-welljio-tint" : "border-slate-200"}`}>
                <input type="radio" name="pm" value="razorpay" checked={form.payment_method === "razorpay"} onChange={change("payment_method")} className="accent-welljio-ink mt-1" />
                <div>
                  <p className="font-medium flex items-center gap-2"><CreditCard className="w-4 h-4" strokeWidth={1.5}/> Razorpay <span className="text-xs px-2 py-0.5 rounded-full bg-welljio-highlight text-welljio-accent">Mock</span></p>
                  <p className="text-xs text-welljio-muted mt-1">UPI, cards, netbanking — simulated for demo.</p>
                </div>
              </label>
              <label data-testid="payment-cod" className={`cursor-pointer border rounded-2xl p-5 flex items-start gap-3 ${form.payment_method === "cod" ? "border-welljio-ink bg-welljio-tint" : "border-slate-200"}`}>
                <input type="radio" name="pm" value="cod" checked={form.payment_method === "cod"} onChange={change("payment_method")} className="accent-welljio-ink mt-1" />
                <div>
                  <p className="font-medium flex items-center gap-2"><Truck className="w-4 h-4" strokeWidth={1.5}/> Cash on Delivery</p>
                  <p className="text-xs text-welljio-muted mt-1">Pay when your order arrives.</p>
                </div>
              </label>
            </div>
          </section>

          <button
            data-testid="checkout-pay-button"
            disabled={loading || cart.items.length === 0}
            type="submit"
            className="w-full bg-welljio-ink text-white rounded-full py-5 font-medium hover:bg-slate-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Processing...</> : `Pay ${inr(cart.total)}`}
          </button>
        </form>

        <aside className="bg-white border border-slate-100 rounded-3xl p-7 h-fit space-y-4 sticky top-28" data-testid="checkout-summary">
          <h2 className="font-heading text-xl font-semibold">Your order</h2>
          {cart.items.length === 0 && <p className="text-welljio-muted text-sm">Your cart is empty.</p>}
          {cart.items.map(({ product, quantity, line_total }) => (
            <div key={product.id} className="flex gap-3">
              <img src={product.images[0]} alt={product.name} className="w-14 h-14 rounded-xl object-cover bg-welljio-tint" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{product.name}</p>
                <p className="text-xs text-welljio-muted">Qty {quantity}</p>
              </div>
              <p className="text-sm font-medium">{inr(line_total)}</p>
            </div>
          ))}
          <div className="border-t border-slate-100 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-welljio-muted"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
            <div className="flex justify-between text-welljio-muted"><span>Shipping</span><span>{cart.shipping === 0 ? "Free" : inr(cart.shipping)}</span></div>
            <div className="flex justify-between font-heading text-lg pt-2"><span>Total</span><span>{inr(cart.total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
